import React, { useEffect, useState, useCallback } from 'react';
import { ethers } from 'ethers';
import { Link } from 'react-router-dom';
import { provider, ethersUtils, createContract, getAccounts, initializeEthers } from './EthersSetup';
import OracleBet from '../deployedContractsInfo/OracleBet.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import useBlockchainEvents from '../hooks/useBlockchainEvents';
import { AddressDisplayLong } from './AddressDisplay.js';

const OracleBetGame = () => {
  const [loading, setLoading] = useState(true);
  const [rulesCollapsed, setRulesCollapsed] = useState(true);
  const [isWalletInitialized, setIsWalletInitialized] = useState(false);
  const [userAddress, setUserAddress] = useState('');
  
  // Contract state
  const [oracleBetContract, setOracleBetContract] = useState(null);
  const [ethersContract, setEthersContract] = useState(null);

  // Initialize wallet first
  useEffect(() => {
    const initWallet = async () => {
      try {
        await initializeEthers();
        const accounts = await getAccounts();
        if (accounts.length > 0) {
          setUserAddress(accounts[0]);
        }
        setIsWalletInitialized(true);
      } catch (error) {
        console.error('OracleBet: Failed to initialize wallet:', error);
        setIsWalletInitialized(true); // Still allow component to continue
      }
    };
    initWallet();
  }, []);

  // Set browser tab title for the game
  useEffect(() => {
    const originalTitle = document.title;
    document.title = `Oracle Betting`;
    
    // Cleanup: restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, []);

  // Initialize contract and load initial data
  useEffect(() => {
    if (!isWalletInitialized) return;

    const initializeContract = async () => {
      try {
        const contract = await createContract(contractAddresses.OracleBet, OracleBet.abi);
        setOracleBetContract(contract);

        // Create ethers contract for events
        if (provider) {
          const ethersContract = new ethers.Contract(contractAddresses.OracleBet, OracleBet.abi, provider);
          setEthersContract(ethersContract);
          console.log('🔧 OracleBet: Ethers contract created');
        } else {
          console.log('🔧 OracleBet: No provider available for ethers contract');
        }

        setLoading(false);
      } catch (error) {
        console.error('Error creating OracleBet contract:', error);
        setOracleBetContract(null);
        setEthersContract(null);
        setLoading(false);
      }
    };

    initializeContract();
  }, [isWalletInitialized]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isWalletInitialized) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-4xl font-bold text-gray-600 mb-4">Initializing...</h1>
        <p className="text-gray-500">Connecting to wallet...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Top Navigation Bar */}
        <div className="flex justify-between items-center mb-6">
          <Link
            to="/asymmetric"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm transition"
          >
            ← Back to Games
          </Link>
        </div>

        <h1 className="text-4xl font-bold text-center mb-8 text-gray-800">
          Oracle Betting
        </h1>

        {/* Game Rules - Collapsible */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <button 
            onClick={() => setRulesCollapsed(!rulesCollapsed)}
            className="w-full flex items-center justify-between text-xl font-semibold text-gray-700 cursor-pointer"
          >
            <span>How Oracle Betting Works</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${rulesCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            rulesCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100 mt-4'
          }`}>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-blue-600">Concept</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>Bet on external oracle outcomes (true/false predictions)</li>
                  <li>Future integration with sports events, weather data, market results</li>
                  <li>Decentralized betting on real-world events</li>
                  <li>Oracle provides trusted external data to smart contract</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-red-600">Current Status</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li><strong>Placeholder Contract:</strong> Basic structure in place</li>
                  <li><strong>No Functionality:</strong> Not yet fully implemented</li>
                  <li><strong>Future Development:</strong> Will support various oracle integrations</li>
                  <li><strong>Coming Soon:</strong> Betting interface and oracle connection</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Placeholder Status */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-yellow-800">
                Development Placeholder
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>This is a placeholder contract for Oracle Betting functionality. The following features are planned for future development:</p>
              </div>
            </div>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded border">
              <h4 className="font-medium text-gray-900 mb-2">Contract Features (Planned)</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Oracle source configuration</li>
                <li>• True/False outcome betting</li>
                <li>• Automatic bet resolution</li>
                <li>• Payout distribution</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded border">
              <h4 className="font-medium text-gray-900 mb-2">Future Integrations</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Sports game outcomes</li>
                <li>• Weather predictions</li>
                <li>• Market price movements</li>
                <li>• Election results</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contract Information */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Contract Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded">
              <p className="text-sm text-gray-600">Contract Address</p>
              <p className="font-mono text-sm break-all">
                <AddressDisplayLong address={
                  contractAddresses.OracleBet && contractAddresses.OracleBet !== '0x0000000000000000000000000000000000000000' 
                    ? contractAddresses.OracleBet 
                    : 'Not deployed'
                } />
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded">
              <p className="text-sm text-gray-600">Status</p>
              <p className="text-lg font-semibold text-yellow-600">
                {contractAddresses.OracleBet && contractAddresses.OracleBet !== '0x0000000000000000000000000000000000000000' 
                  ? 'Contract Loaded' 
                  : 'Not Deployed'}
              </p>
            </div>
          </div>
          <div className="mt-4 text-center">
            <p className="text-gray-500 italic">
              Full Oracle Betting functionality coming soon! This contract is currently a development placeholder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OracleBetGame;